import { NavController, AlertController, LoadingController, ModalController, PopoverController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { UntypedFormGroup, UntypedFormControl, UntypedFormBuilder } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { BaseService } from '../shared/services/base.service';
import { ConfirmationPopoverComponent } from '../shared/components/confirmation-popover/confirmation-popover.component';

export class BasePage {
  /** Class members */
  protected alertCtrl: AlertController;

  // Subject for automatic subscription cleanup: https://goo.gl/bzFQT4
  protected unsubscribe: Subject<void> = new Subject();

  protected loadingCtrl: LoadingController;

  protected loading: any;

  protected modalCtrl: ModalController;

  protected popoverCtrl: PopoverController;

  protected fb: UntypedFormBuilder;

  constructor(readonly pageName: string) {}

  /**
   * Method to show alert
   *
   * @param title - The main title in the heading of the alert
   * @param message - The main message to be displayed in the alert
   * @param handler - Handler to handle action on "Ok" button click
   */
  public async showAlert(title: string, message: string, handler?: () => any) {
    const alert = await this.alertCtrl.create({
      header: title,
      message,
      buttons: [{ text: 'Ok', handler: handler || (() => {}) }],
    });
    await alert.present();
    return alert;
  }

  public async confirmAlert(message: string, handler: () => any, buttons?, header?, backdrop?) {
    const alert = await this.alertCtrl.create({
      header: header || 'Confirmation',
      message,
      buttons: buttons || [{ text: 'Cancel' }, { text: 'Ok', handler }],
      backdropDismiss: backdrop !== false,
    });

    await alert.present();
  }

  public async showLoading(message?: string): Promise<any> {
    message = message || 'Loading...';

    this.loading = await this.loadingCtrl.create({
      message,
    });

    return await this.loading.present();
  }

  public async hideLoading() {
    if (this.loading) {
      return await this.loading.dismiss();
    }
  }

  public closeModal(returnedValue: any = null): Promise<any> {
    return this.modalCtrl.dismiss(returnedValue);
  }

  public showOkAlert(message: string, resetOption?: string, handler?: () => any) {
    return this.okAlert(message, resetOption, handler).then((alert) => {
      alert.present();
    });
  }

  public showErrorAlert(message: string, handler?: () => any) {
    return this.errorAlert(message, handler).then((alert) => {
      alert.present();
    });
  }

  protected async showConfirmationPopover(
    props: {
      header: {
        text: string;
        level?: string;
        icon?: string;
      };
      description?: string[];
      buttons?: { text: string; type?: string; handler: any }[];
    },
    onDismiss?: any,
  ) {
    const popover = await this.popoverCtrl.create({
      component: ConfirmationPopoverComponent,
      componentProps: { props },
      translucent: true,
    });
    if (onDismiss) {
      popover.onDidDismiss().then(onDismiss);
    }
    return await popover.present();
  }

  protected keys(obj: any): Array<any> {
    return Object.keys(obj);
  }

  protected okAlert(message: string, resetOption?: string, handler?: () => any) {
    const header =
      environment.environmentName !== 'production' && resetOption === 'SMS' ? 'Check your mobile phone' : 'Success';
    return this.alertCtrl.create({
      header,
      message,
      buttons: [{ text: 'Ok', handler: handler || (() => {}) }],
    });
  }

  protected errorAlert(message: string, handler?: () => any) {
    return this.alertCtrl.create({
      header: 'Error',
      message,
      buttons: [{ text: 'Ok', handler: handler || (() => {}) }],
    });
  }

  protected alertFor(err: any, service: BaseService, navCtrl: NavController) {
    const handler = () => {
      service['refresh']();
      navCtrl.navigateRoot('');
    };
    const defaultAlert = this.errorAlert(
      'There has been an error with the request. The app will now sync to update your local data.',
      handler,
    );

    switch (err.type) {
      // Client-side error
      case 'error':
        // Network error, allow retry (https://fetch.spec.whatwg.org/#concept-response-type)
        if (err.status === 0) {
          return this.errorAlert(
            "Looks like there's no Internet access. Please double check your connection and try again.",
          );
        }
        return defaultAlert;
      // Server-side error
      case 'default':
        return this.errorAlert(
          'The server returned an error.The app will now sync to update your local data.',
          handler,
        );
      default:
        return defaultAlert;
    }
  }

  protected ionViewDidLeave(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  addFormGroupControls(form, group, controls) {
    const fGroup = <UntypedFormGroup>form.get(group);
    if (fGroup) {
      Object.keys(controls).forEach((key) => {
        fGroup.addControl(key, new UntypedFormControl(controls[key][0], controls[key][1]));
      });
    } else {
      form.addControl(group, this.fb.group(controls));
    }
  }
}
